import React, { useState, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, CheckCheck, Play, Pause, Download, FileText, Image as ImageIcon } from 'lucide-react';
import PostPreview from '../PostPreview';

interface MessageBubbleProps {
  content: string;
  isCurrentUser: boolean;
  senderName?: string;
  senderAvatar?: string;
  timestamp: string;
  isRead?: boolean;
  isDelivered?: boolean;
  showAvatar?: boolean;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  messageType?: 'text' | 'file' | 'audio' | 'image';
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  mediaDuration?: number;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  content,
  isCurrentUser,
  senderName,
  senderAvatar,
  timestamp,
  isRead = false,
  isDelivered = false,
  showAvatar = true,
  isFirstInGroup = true,
  isLastInGroup = true,
  messageType = 'text',
  fileUrl,
  fileName,
  fileType,
  fileSize,
  mediaDuration
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(mediaDuration || 0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && !duration) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const downloadFile = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Helper functions to detect shared posts
  const isSharedPost = (text: string): boolean => {
    const postLinkPattern = /\/app\/post\/([a-f0-9-]{36})/i;
    return postLinkPattern.test(text);
  };

  const extractPostId = (text: string): string | null => {
    const postLinkPattern = /\/app\/post\/([a-f0-9-]{36})/i;
    const match = text.match(postLinkPattern);
    return match ? match[1] : null;
  };

  const renderContent = () => {
    if (messageType === 'text') {
      const hasPostLink = isSharedPost(content);
      const postId = hasPostLink ? extractPostId(content) : null;
      
      if (hasPostLink && postId) {
        return (
          <div className="space-y-2">
            {content.trim() !== `/app/post/${postId}` && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {content.replace(/\/app\/post\/[a-f0-9-]{36}/i, '').trim()}
              </p>
            )}
            <PostPreview postId={postId} />
          </div>
        );
      }
      
      return (
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {content}
        </p>
      );
    }

    switch (messageType) {
      case 'image':
        return (
          <div className="space-y-2">
            {fileUrl && (
              <div className="relative rounded-lg overflow-hidden max-w-xs">
                <img 
                  src={fileUrl} 
                  alt={fileName || 'Image'} 
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            )}
            {content && (
              <p className="text-sm">{content}</p>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="flex items-center space-x-3 min-w-[200px]">
            <Button
              onClick={handlePlayPause}
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-full hover:bg-primary/10"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5" />
              )}
            </Button>
            
            <div className="flex-1 space-y-1">
              <div className="flex items-center space-x-2">
                <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ 
                      width: duration > 0 ? 
                        `${(currentTime / duration) * 100}%` : 
                        '0%' 
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatDuration(Math.floor(currentTime))}</span>
                <span>{formatDuration(Math.floor(duration))}</span>
              </div>
            </div>

            {fileUrl && (
              <audio
                ref={audioRef}
                src={fileUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                preload="metadata"
              />
            )}
          </div>
        );

      case 'file':
        return (
          <div className="flex items-center space-x-3 min-w-[200px] p-3 glass rounded-lg border border-border/30">
            <div className="h-10 w-10 rounded-lg glass border border-border/30 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{fileName}</p>
              <p className="text-xs text-muted-foreground">
                {fileSize ? formatFileSize(fileSize) : 'Unknown size'}
              </p>
            </div>
            
            <Button
              onClick={downloadFile}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 rounded-full hover:bg-primary/10"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        );

      default:
        return <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>;
    }
  };

  return (
    <div className={cn(
      "flex items-end space-x-2 mb-2 px-2",
      isCurrentUser ? "justify-end" : "justify-start"
    )}>
      {/* Avatar per messaggi degli altri utenti */}
      {!isCurrentUser && showAvatar && (
        <Avatar className="h-8 w-8 ring-1 ring-border/30">
          <AvatarImage src={senderAvatar || ''} />
          <AvatarFallback className="text-xs bg-muted">
            {senderName?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}
      
      {!isCurrentUser && !showAvatar && (
        <div className="w-8" />
      )}

      <div className={cn(
        "flex flex-col max-w-[70%] md:max-w-[60%]",
        isCurrentUser ? "items-end" : "items-start"
      )}>
        {/* Nome mittente per messaggi di gruppo */}
        {!isCurrentUser && isFirstInGroup && senderName && (
          <span className="text-xs text-muted-foreground mb-1 px-3">
            {senderName}
          </span>
        )}

        {/* Message Bubble - Modern WhatsApp Style */}
        <div className={cn(
          "relative px-4 py-2.5 max-w-full break-words transition-all hover:shadow-lg",
          isCurrentUser ? (
            "gradient-primary text-primary-foreground shadow-primary/30 shadow-md"
          ) : (
            "glass border border-border/20 hover:border-border/40"
          ),
          // Modern rounded corners - more like WhatsApp
          isFirstInGroup && isLastInGroup ? "rounded-2xl" :
          isFirstInGroup ? (
            isCurrentUser ? "rounded-2xl rounded-br-lg" : "rounded-2xl rounded-bl-lg"
          ) : isLastInGroup ? (
            isCurrentUser ? "rounded-2xl rounded-tr-lg" : "rounded-2xl rounded-tl-lg"
          ) : (
            isCurrentUser ? "rounded-l-2xl rounded-r-lg" : "rounded-r-2xl rounded-l-lg"
          )
        )}>
          {renderContent()}
        </div>

        {/* Timestamp e status di lettura */}
        {isLastInGroup && (
          <div className={cn(
            "flex items-center space-x-1 mt-1 px-2",
            isCurrentUser ? "justify-end" : "justify-start"
          )}>
            <span className="text-xs text-muted-foreground">
              {timestamp}
            </span>
            {isCurrentUser && (
              <div className="flex items-center">
                {isRead ? (
                  <CheckCheck className="w-4 h-4 text-primary" />
                ) : isDelivered ? (
                  <CheckCheck className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Check className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;